import {
  SetStateAction,
  ChangeEvent,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubFormControl,
  DevHubFormControlLabel,
  DevHubFormLabel,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubLink,
  DevHubPaper,
  DevHubRadio,
  DevHubRadioGroup,
  DevHubTextField,
  DevHubTooltip,
} from '@devxdiscover/devhub-ui';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Content, Header, Page } from '@backstage/core-components';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { useApolloClient } from '@apollo/client';
import {
  GetApplicationsByUserQuery,
  GetApplicationsQuery,
  GetComponentAPIsQuery,
  GetComponentsQuery,
  GetDevicesQuery,
} from './queries';
import {
  CatalogQueryType,
  CatalogSearchResultData,
  CatalogSearchResultItem,
  CatalogStandardMapping,
  ColumnDefinition,
  OwnerFilterType,
  ResourceCatalogComponentPaged,
} from './types';
import {
  ComponentDetails,
  convertComponentSearchResult,
} from './ComponentDetails';
import { convertDeviceSearchResult, DeviceDetails } from './DeviceDetails';
import { ApiDetails, convertApiSearchResult } from './ApiDetails';
import {
  ApplicationDetails,
  convertApplicationSearchResult,
} from './ApplicationDetails';
import CircularProgress from '@mui/material/CircularProgress';
import LaunchIcon from '@material-ui/icons/Launch';
import Backdrop from '@mui/material/Backdrop';
import { NavLink, useSearchParams } from 'react-router-dom';
import { ownerOrgRenderer } from './fieldRenderers/ownerOrg';
import { applicationLinkRenderer } from './fieldRenderers/applicationLink';
import { descriptionRenderer } from './fieldRenderers/description';
import { apiDefinitionRenderer } from './fieldRenderers/apiDefinition';
import { UserContext } from '../../contexts/UserContext';

const searchResultConverters = {
  [CatalogQueryType.Component]: (data: ResourceCatalogComponentPaged) =>
    convertComponentSearchResult(data, CatalogQueryType.Component),
  [CatalogQueryType.API]: convertApiSearchResult,
  [CatalogQueryType.Device]: convertDeviceSearchResult,
  [CatalogQueryType.Application]: convertApplicationSearchResult,
  [CatalogQueryType.TechDocs]: (data: ResourceCatalogComponentPaged) =>
    convertComponentSearchResult(data, CatalogQueryType.TechDocs),
};

const queries = {
  [CatalogQueryType.Component]: GetComponentsQuery,
  [CatalogQueryType.API]: GetComponentAPIsQuery,
  [CatalogQueryType.Device]: GetDevicesQuery,
  [CatalogQueryType.Application]: GetApplicationsQuery,
  [CatalogQueryType.TechDocs]: GetComponentsQuery,
};

const columns: Record<CatalogQueryType, ColumnDefinition[]> = {
  [CatalogQueryType.Component]: [
    {
      field: 'ownerOrg',
      header: 'Owner',
      width: '25%',
      renderer: ownerOrgRenderer,
    },
    {
      field: 'description',
      header: 'Description',
      width: '30%',
      renderer: descriptionRenderer,
    },
  ],
  [CatalogQueryType.TechDocs]: [
    {
      field: 'ownerOrg',
      header: 'Owner',
      width: '25%',
      renderer: ownerOrgRenderer,
    },
    {
      field: 'description',
      header: 'Description',
      width: '30%',
      renderer: descriptionRenderer,
    },
  ],
  [CatalogQueryType.API]: [
    {
      field: 'ownerOrg',
      header: 'Owner',
      width: '25%',
      renderer: ownerOrgRenderer,
    },
    { field: 'kind', header: 'Kind', width: '10%' },
    {
      field: 'definition',
      header: 'Definition',
      width: '5%',
      renderer: apiDefinitionRenderer,
    },
  ],
  [CatalogQueryType.Device]: [
    { field: 'ipAddress', header: 'IP Address', width: '10%' },
    { field: 'os', header: 'OS', width: '10%' },
    {
      field: 'application',
      header: 'Application(s)',
      width: '20%',
      renderer: applicationLinkRenderer,
    },
  ],
  [CatalogQueryType.Application]: [
    { field: 'id', header: 'Application ID', width: '15%' },
    { field: 'owner', header: 'Owner', width: '30%' },
    { field: 'status', header: 'Status', width: '10%' },
  ],
};

const rowDetailForRow = (row: CatalogSearchResultItem) => {
  switch (row.type) {
    case CatalogQueryType.Component:
      return <ComponentDetails {...row} />;
    case CatalogQueryType.API:
      return <ApiDetails {...row} />;
    case CatalogQueryType.Device:
      return <DeviceDetails {...row} />;
    case CatalogQueryType.Application:
      return <ApplicationDetails {...row} />;
    default:
      return undefined;
  }
};

const linkTemplateForRow = (row: CatalogSearchResultItem) => {
  switch (row.type) {
    case CatalogQueryType.Component:
      return (
        <DevHubLink
          component={NavLink}
          to={`/catalog/default/component/${row?.id}`}
        >
          {row?.name}
        </DevHubLink>
      );
    case CatalogQueryType.TechDocs:
      return (
        <DevHubLink
          component={NavLink}
          to={`/docs/default/component/${row?.id}`}
        >
          {row?.name}
        </DevHubLink>
      );
    case CatalogQueryType.API:
      return (
        <DevHubLink component={NavLink} to={`/catalog/default/api/${row?.id}`}>
          {row?.title}
        </DevHubLink>
      );
    case CatalogQueryType.Device: {
      if (!('href' in row?.item)) return row?.name;
      const href = row.item.href;
      const tip = href.startsWith('https://comcast.service-now.com/')
        ? 'View in IOP'
        : 'View';
      return (
        <>
          {row?.name}
          <DevHubTooltip title={tip}>
            <DevHubIconButton
              size="small"
              onClick={() => window.open(href, '_blank')}
            >
              <LaunchIcon color="primary" fontSize="small" />
            </DevHubIconButton>
          </DevHubTooltip>
        </>
      );
    }
    case CatalogQueryType.Application:
      return (
        <DevHubLink
          component={NavLink}
          to={`/catalog/default/system/${row?.id}`}
        >
          {row.name}
        </DevHubLink>
      );
    default:
      return <></>;
  }
};

const queryParamsForType = (
  filterType: CatalogQueryType,
  searchTerm?: string,
  userLogin?: string,
  starredApplications?: number[],
) => {
  switch (filterType) {
    case CatalogQueryType.Component:
    case CatalogQueryType.API:
      return {
        filter: {
          name: searchTerm,
          userLogin: userLogin,
        },
      };
    case CatalogQueryType.TechDocs:
      return {
        filter: {
          name: searchTerm,
          hasTechDocsRef: true,
        },
      };
    case CatalogQueryType.Device:
      if (!searchTerm) {
        return {};
      }
      return {
        filter: {
          anyOf: {
            fqdns: [searchTerm],
            ipAddresses: [searchTerm],
            serialNumbers: [searchTerm],
          },
        },
      };
    case CatalogQueryType.Application:
      if (userLogin) {
        return {
          login: userLogin,
          filter: {
            applicationName: searchTerm,
          },
        };
      }
      return {
        filter: {
          applicationName: searchTerm,
          applicationIds: starredApplications,
        },
        sortBy: {
          field: 'Name',
          direction: 'Asc',
        },
      };
    default:
      return {};
  }
};

// Function to map different query results to StandardType
function mapToStandardType(
  props: CatalogStandardMapping,
): CatalogSearchResultData {
  const { data, queryType } = props;
  return searchResultConverters[queryType](data as any);
}

/**
 * CatalogSearch component to searcha and display results from the catalog
 */
export const CatalogSearch = () => {
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);
  const [data, setData] = useState<CatalogSearchResultItem[]>(
    [] as CatalogSearchResultItem[],
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<CatalogQueryType | undefined>(
    undefined,
  );
  const [ownedFilterType, setOwnedFilterType] = useState<OwnerFilterType>(
    OwnerFilterType.All,
  );
  const ITEMS_PER_PAGE = 20;
  const [allStarredAppsIds, setAllStarredAppIds] = useState<number[]>([]);
  const [firstItem, setFirstItem] = useState(0); // PrimeReact DataTable pages are zero-indexed
  const [currentPage, setCurrentPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchClicked, setSearchClicked] = useState(false);
  const [searchParams] = useSearchParams();
  const client = useApolloClient();
  const { login } = useContext(UserContext);
  const [isStarred, setIsStarred] = useState<boolean>(
    !!localStorage
      .getItem('starredApplications')
      ?.includes(String(data[0]?.id)),
  );

  const resetPagination = () => {
    window.scrollTo(0, 0);
    setCurrentPage(0);
    setFirstItem(0);
  };

  const getStarredAppIDs = () => {
    const currentArray = localStorage.getItem('starredApplications');
    const starredAppIds = currentArray ? JSON.parse(currentArray) : [];
    setAllStarredAppIds(starredAppIds);
  };

  // Adjust the PrimeReact theme based on the theme stored in local storage
  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    let filterBy = filterType;
    const filterByParam = searchParams.get('filterBy');
    if (!filterBy && filterByParam) {
      filterBy = Object.values(CatalogQueryType).find(
        x => x.toUpperCase() === filterByParam.toUpperCase(),
      ) as CatalogQueryType;
    }
    if (!filterBy) {
      filterBy = CatalogQueryType.Component;
    }
    if (!filterType && filterBy) {
      setFilterType(filterBy as CatalogQueryType);
    }
    const params = queryParamsForType(
      filterBy,
      searchTerm === '' ? undefined : searchTerm,
      ownedFilterType === OwnerFilterType.Owned ? login : undefined,
      ownedFilterType === OwnerFilterType.Starred &&
        allStarredAppsIds.length > 0
        ? allStarredAppsIds
        : undefined,
    );

    const query =
      filterBy === CatalogQueryType.Application &&
      ownedFilterType === OwnerFilterType.Owned
        ? GetApplicationsByUserQuery
        : queries[filterBy];

    setIsFetching(true);
    client
      .query({
        query: query,
        variables: {
          pagination: {
            limit: ITEMS_PER_PAGE,
            offset: currentPage * ITEMS_PER_PAGE,
          },
          ...params,
        },
      })
      .then(response => {
        const result = {
          data: response?.data?.identity
            ? response?.data?.identity?.user
            : response.data,
          queryType: filterBy,
        } as CatalogStandardMapping;

        const standardData = mapToStandardType(result);
        setData(standardData.items);
        setTotalRecords(standardData.totalCount);
      })
      .catch()
      .finally(() => setIsFetching(false));

    setSearchClicked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, currentPage, searchClicked, searchParams, ownedFilterType]);

  const handleSearch = () => {
    resetPagination();
    setSearchClicked(true);
  };

  const onPaginate = (e: { first: SetStateAction<number> }) => {
    setCurrentPage((e.first as number) / ITEMS_PER_PAGE);
    setFirstItem(e.first);
    window.scrollTo(0, 0);
  };

  const getColumns = () => {
    return columns[filterType ?? CatalogQueryType.Component];
  };

  const clearSearch = () => {
    setSearchTerm('');

    if (searchTerm !== '') {
      handleSearch();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = (event.target as HTMLInputElement).value;
    setData([]);
    resetPagination();
    setFilterType(selectedValue as CatalogQueryType);
  };

  const handleOwnedFilterTypeChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedValue = (event.target as HTMLInputElement).value;
    if (selectedValue === OwnerFilterType.Starred) {
      getStarredAppIDs();
    }
    setData([]);
    resetPagination();
    setOwnedFilterType(selectedValue as OwnerFilterType);
  };

  const toggleStar = (rowDataID: any) => {
    const currentArray = localStorage.getItem('starredApplications');
    const newArray = currentArray ? JSON.parse(currentArray) : [];

    const index = newArray.indexOf(rowDataID);
    if (index !== -1) {
      // Remove the ID if it already exists
      newArray.splice(index, 1);
    } else {
      // Add the ID if it doesn't exist
      newArray.push(rowDataID);
    }
    localStorage.setItem('starredApplications', JSON.stringify(newArray));
    setIsStarred(!isStarred);
  };

  const getOwnedFilterType = (queryType: CatalogQueryType) => {
    switch (queryType) {
      case CatalogQueryType.Application:
        return [
          OwnerFilterType.All,
          OwnerFilterType.Owned,
          OwnerFilterType.Starred,
        ];
      case CatalogQueryType.API:
      case CatalogQueryType.Component:
        return [OwnerFilterType.All, OwnerFilterType.Owned];
      default:
        return [];
    }
  };

  const canFilterOwner =
    filterType === CatalogQueryType.Component ||
    filterType === CatalogQueryType.Application ||
    filterType === CatalogQueryType.API;

  const starButtonTemplate = (rowData: any) => {
    const isAppStarred = JSON.parse(
      localStorage.getItem('starredApplications') || '[]',
    ).includes(rowData.id);

    return (
      <DevHubIconButton onClick={() => toggleStar(rowData.id)}>
        <DevHubDynamicIcon
          iconName="StarSharp"
          fontSize="medium"
          color={isAppStarred ? 'gold' : 'disabled'}
        />
      </DevHubIconButton>
    );
  };

  return (
    <Page themeId="home">
      <Header
        style={{ whiteSpace: 'nowrap' }}
        title="DevHub Catalog"
        subtitle="Search for items in the catalog"
      />
      <Content>
        <DevHubPaper>
          {filterType && (
            <DevHubBox p={2}>
              <DevHubGrid container alignItems="center">
                <DevHubGrid item xs={12} lg={6}>
                  <DevHubFormControl component="fieldset">
                    <DevHubFormLabel component="legend">
                      Filter / Search by Type
                    </DevHubFormLabel>
                    <DevHubRadioGroup
                      aria-label="filter"
                      name="filter1"
                      value={filterType}
                      onChange={handleChange}
                      row
                    >
                      {Object.values(CatalogQueryType)
                        .filter(
                          queryType => queryType !== CatalogQueryType.TechDocs,
                        )
                        .map(queryType => (
                          <DevHubFormControlLabel
                            key={queryType}
                            value={queryType}
                            control={<DevHubRadio disabled={isFetching} />}
                            label={queryType}
                          />
                        ))}
                    </DevHubRadioGroup>
                  </DevHubFormControl>
                </DevHubGrid>
                <DevHubGrid item xs={12} lg={6}>
                  <DevHubBox display="flex" alignItems="center">
                    <DevHubTextField
                      id="catalog-search"
                      label="Search"
                      margin="normal"
                      variant="outlined"
                      value={searchTerm}
                      disabled={isFetching}
                      noMargin
                      placeholder="Enter your search text. Use * as a wildcard."
                      onChange={(e: {
                        target: { value: SetStateAction<string> };
                      }) => setSearchTerm(e.target.value)}
                      onKeyDown={(e: { key: string }) =>
                        e.key === 'Enter' && handleSearch()
                      }
                      size="small"
                      InputProps={
                        searchTerm
                          ? {
                              endAdornment: (
                                <DevHubInputAdornment position="end">
                                  <DevHubIconButton onClick={clearSearch}>
                                    <DevHubDynamicIcon iconName="Clear" />
                                  </DevHubIconButton>
                                </DevHubInputAdornment>
                              ),
                            }
                          : {}
                      }
                    />
                    <DevHubButton
                      variant="contained"
                      buttonColor="black"
                      size="large"
                      height="large"
                      href="#"
                      disabled={isFetching}
                      onClick={handleSearch}
                    >
                      Search&nbsp;&nbsp;
                      <DevHubDynamicIcon iconName="Search" />
                    </DevHubButton>
                  </DevHubBox>
                </DevHubGrid>
                {canFilterOwner && (
                  <DevHubGrid item xs={12}>
                    <DevHubFormControl component="fieldset">
                      <DevHubFormLabel component="legend">
                        {filterType === CatalogQueryType.Application
                          ? 'Filter by Owned / Starred'
                          : 'Filter by Owned'}
                      </DevHubFormLabel>
                      <DevHubRadioGroup
                        aria-label="filter"
                        name="filter2"
                        value={ownedFilterType}
                        onChange={handleOwnedFilterTypeChange}
                        row
                      >
                        {getOwnedFilterType(filterType).map(ownedType => (
                          <DevHubFormControlLabel
                            key={ownedType}
                            value={ownedType}
                            disabled={
                              filterType !== CatalogQueryType.Application &&
                              ownedType === OwnerFilterType.Starred
                            }
                            control={<DevHubRadio disabled={isFetching} />}
                            label={ownedType}
                          />
                        ))}
                      </DevHubRadioGroup>
                    </DevHubFormControl>
                  </DevHubGrid>
                )}
              </DevHubGrid>
            </DevHubBox>
          )}
        </DevHubPaper>
        <DevHubPaper>
          {filterType && (
            <DevHubGrid container>
              <DevHubGrid item xs={12}>
                <DevHubBox
                  sx={{
                    minHeight: '15rem',
                    width: '100%',
                  }}
                >
                  <div className="card">
                    <div style={{ position: 'relative' }}>
                      <Backdrop
                        open={isFetching}
                        style={{
                          color: '#fff',
                          zIndex: 10,
                          position: 'absolute',
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            margin: '10rem auto',
                            width: 'fit-content',
                          }}
                        >
                          <CircularProgress color="inherit" />
                        </div>
                      </Backdrop>
                      <DataTable
                        value={data}
                        className="p-datatable-striped"
                        onRowToggle={e => setExpandedRows(e.data)}
                        expandedRows={expandedRows}
                        rowExpansionTemplate={rowDetailForRow}
                        showGridlines
                        paginator
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                        totalRecords={totalRecords}
                        onPage={onPaginate}
                        first={firstItem}
                        rows={ITEMS_PER_PAGE}
                        lazy
                        loading={isFetching}
                        stateKey="catalog-search"
                        stateStorage="session"
                      >
                        <Column
                          expander={row => row.expandable}
                          style={{ width: '1%' }}
                        />
                        <Column
                          field="name"
                          header="Name"
                          body={linkTemplateForRow}
                          style={{ width: '25%' }}
                        />
                        {getColumns().map((col, index) => {
                          if (col?.renderer) {
                            return col.renderer(col, index);
                          }
                          return (
                            <Column
                              key={index}
                              field={col.field}
                              header={col.header}
                              style={{ width: col.width }}
                            />
                          );
                        })}
                        {filterType === CatalogQueryType.Application && (
                          <Column
                            header="Starred"
                            body={starButtonTemplate}
                            style={{ width: '1%' }}
                          />
                        )}
                      </DataTable>
                    </div>
                  </div>
                </DevHubBox>
              </DevHubGrid>
            </DevHubGrid>
          )}
        </DevHubPaper>
      </Content>
    </Page>
  );
};
